import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    NgZone,
    OnDestroy,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { InputTextComponent } from 'clicklease-ui-elements';

/**
 * @description Parsed address emitted after the user selects a suggestion. The
 * shape mirrors the object the forms' previous `parsePlace` helper produced, so
 * their store wiring stays unchanged.
 */
export interface AddressResult {
    street: string;
    city: string;
    state: string;
    zipcode: string;
    county: string;
}

const EMPTY_ADDRESS_RESULT: AddressResult = {
    street: '',
    city: '',
    state: '',
    zipcode: '',
    county: '',
};

/**
 * @component
 * @class AddressAutocompleteComponent
 * @description Inline Google Places address autocomplete (ROS-2078).
 *
 * Replaces the <gmp-place-autocomplete> web component, which opens a full-screen
 * overlay on mobile. The overlay sizes itself to 100vh before the iOS keyboard
 * opens and cannot reposition afterwards (closed shadow DOM), so the input and
 * back arrow end up above the visible viewport.
 *
 * The visible field is the design-system clkUiElements-input-text — the same
 * Material input the rest of these forms use — so label, error and styling
 * behave identically to every other field. This wrapper adds the suggestions
 * dropdown, driven by the Places (New) AutocompleteSuggestion data API (the
 * programmatic equivalent of the widget: same PlacePrediction/Place objects,
 * same billing SKU when session tokens are used). The dropdown is sized to the
 * space that remains visible above the mobile keyboard.
 *
 * The parent form owns the street FormControl and passes it in via `control`,
 * so validators and value restoration stay exactly where they were.
 */
@Component({
    selector: 'rose-portal-address-autocomplete',
    standalone: true,
    imports: [InputTextComponent],
    templateUrl: './address-autocomplete.component.html',
    styleUrl: './address-autocomplete.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class AddressAutocompleteComponent implements AfterViewInit, OnDestroy {
    /** The street FormControl owned by the parent form (passed through to the inner input). */
    @Input({ required: true }) control!: FormControl<string | null>;
    @Input() elementId: string = '';
    @Input() label: string = '';
    @Input() placeholder: string = '';
    @Input() errorRequired: string = '';
    /** Prefix icon id (clkUiElements-icon), passed through to the inner input. */
    @Input() iconId: string = '';

    /** Emits the parsed address after the user picks a suggestion. */
    @Output() placeResult = new EventEmitter<AddressResult>();
    /** Re-emits raw input events so the parent can clear dependent fields. */
    @Output() inputChanged = new EventEmitter<string>();

    @ViewChild('dropdown') private dropdownRef?: ElementRef<HTMLElement>;

    /** View models: original prediction + main text split into matched/unmatched segments. */
    suggestions: Array<{
        prediction: any;
        mainSegments: Array<{ text: string; matched: boolean }>;
        secondaryText: string;
    }> = [];
    highlightedIndex: number = -1;

    readonly #host = inject<ElementRef<HTMLElement>>(ElementRef);
    readonly #zone = inject(NgZone);
    readonly #cdr = inject(ChangeDetectorRef);

    /**
     * Groups keystrokes and the final selection into one billable Autocomplete
     * session (the widget did this automatically; the data API does not).
     */
    #sessionToken: any = null;
    #debounceTimer: any = null;
    #focusScrollTimer: any = null;
    /** Discards out-of-order responses from superseded requests. */
    #requestSequence: number = 0;
    /** Last query fetched, to skip re-fetching an identical input (e.g. trailing space). */
    #lastQuery: string = '';
    readonly #onViewportChange = () => this.#sizeDropdownToViewport();

    ngAfterViewInit(): void {
        // Combobox ARIA wiring on the inner matInput (it lives inside the
        // design-system component, so the attributes are set programmatically).
        const input = this.#inputElement();
        input?.setAttribute('role', 'combobox');
        input?.setAttribute('aria-autocomplete', 'list');
        input?.setAttribute('aria-expanded', 'false');
        input?.setAttribute('aria-controls', `${this.elementId}-suggestions-list`);
    }

    ngOnDestroy(): void {
        clearTimeout(this.#debounceTimer);
        clearTimeout(this.#focusScrollTimer);
        this.#detachViewportListeners();
    }

    /** Mirrors the old widget's `.value` contract. */
    get value(): string {
        return this.control?.value ?? '';
    }
    set value(newValue: string) {
        this.control?.setValue(newValue ?? '', { emitEvent: false });
    }

    /** Bound to native input events bubbling out of the inner matInput (user typing only). */
    onUserInput(): void {
        this.inputChanged.emit(this.value);
        const query = this.value.trim();
        if (query.length < 3) {
            this.#closeSuggestions();
            return;
        }
        if (query === this.#lastQuery && this.suggestions.length) {
            return; // same effective query, dropdown already showing its results
        }
        clearTimeout(this.#debounceTimer);
        this.#debounceTimer = setTimeout(() => this.#fetchSuggestions(query), 250);
    }

    onFocusIn(): void {
        this.#attachViewportListeners();
        // Touch devices only: wait for the keyboard animation to start, then
        // bring the input toward the top of the visual viewport so the dropdown
        // has room below it. Desktop keeps its scroll position.
        if (window.matchMedia('(pointer: coarse)').matches) {
            clearTimeout(this.#focusScrollTimer);
            this.#focusScrollTimer = setTimeout(() => {
                this.#inputElement()?.scrollIntoView({ block: 'start', behavior: 'smooth' });
            }, 300);
        }
    }

    onFocusOut(): void {
        this.#detachViewportListeners();
        clearTimeout(this.#focusScrollTimer);
        this.#closeSuggestions();
        // A session abandoned without a selection must not leak its token into a
        // later interaction: expired tokens bill each request individually.
        this.#sessionToken = null;
        // Parity with the previous widget's focusout contract: leaving the field
        // empty clears every dependent address field in the parent form.
        if (this.value === '') {
            this.placeResult.emit({ ...EMPTY_ADDRESS_RESULT });
        }
    }

    onKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            // Keep Enter from triggering implicit form submission.
            event.preventDefault();
            if (this.highlightedIndex >= 0 && this.suggestions.length) {
                this.#selectSuggestion(this.suggestions[this.highlightedIndex]);
            }
            return;
        }
        if (!this.suggestions.length) {
            return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            const lastIndex = this.suggestions.length - 1;
            this.highlightedIndex = Math.min(lastIndex, Math.max(0, this.highlightedIndex + step));
            this.#updateAriaState();
            this.#scrollHighlightedIntoView();
        } else if (event.key === 'Escape') {
            this.#closeSuggestions();
        }
    }

    /**
     * Selection is a two-step dance so touch scrolling still works: mousedown
     * only prevents the input from blurring (keeping the keyboard up), and the
     * actual selection happens on click. On touch devices the synthetic
     * mousedown/click pair fires only for completed taps — a scroll gesture on
     * the list produces neither, so it scrolls naturally.
     */
    onSuggestionMouseDown(event: Event): void {
        event.preventDefault();
    }

    onSuggestionClick(suggestion: { prediction: any }): void {
        this.#selectSuggestion(suggestion);
    }

    async #fetchSuggestions(query: string): Promise<void> {
        const sequence = ++this.#requestSequence;
        this.#lastQuery = query;
        try {
            const places = await (window as any).google.maps.importLibrary('places');
            if (!this.#sessionToken) {
                this.#sessionToken = new places.AutocompleteSessionToken();
            }
            const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                input: query,
                sessionToken: this.#sessionToken,
                includedRegionCodes: ['us'],
                includedPrimaryTypes: [
                    'street_address',
                    'premise',
                    'postal_town',
                    'neighborhood',
                    'sublocality',
                ],
            });
            if (sequence !== this.#requestSequence) {
                return;
            }
            this.suggestions = (suggestions || []).map((suggestion: any) => {
                const prediction = suggestion.placePrediction;
                const mainText = prediction?.mainText ?? prediction?.text;
                return {
                    prediction: suggestion,
                    mainSegments: this.#highlightMatches(mainText?.text ?? '', query),
                    secondaryText: prediction?.secondaryText?.text ?? '',
                };
            });
            this.highlightedIndex = -1;
            this.#updateAriaState();
            this.#cdr.markForCheck();
            setTimeout(() => this.#sizeDropdownToViewport());
        } catch (error) {
            console.error('[Places] fetchAutocompleteSuggestions failed:', error);
            if (sequence === this.#requestSequence) {
                this.#closeSuggestions();
            }
        }
    }

    /**
     * Splits the suggestion text into segments, bolding the parts that literally
     * match the user's typed tokens (case-insensitive), like the legacy widget's
     * .pac-matched emphasis. Deliberately NOT the API's `matches` ranges: those
     * are fuzzy and can span the entire string for spell-corrected or reordered
     * matches, fully bolding suggestions that only loosely relate to the input.
     */
    #highlightMatches(text: string, query: string): Array<{ text: string; matched: boolean }> {
        const tokens = query.trim().toLowerCase().split(/[\s,]+/).filter((token) => token.length > 0);
        if (!text || !tokens.length) {
            return [{ text, matched: false }];
        }
        const lower = text.toLowerCase();
        const ranges: Array<[number, number]> = [];
        for (const token of tokens) {
            let index = lower.indexOf(token);
            while (index !== -1) {
                ranges.push([index, index + token.length]);
                index = lower.indexOf(token, index + token.length);
            }
        }
        if (!ranges.length) {
            return [{ text, matched: false }];
        }
        ranges.sort((a, b) => a[0] - b[0]);
        const merged: Array<[number, number]> = [];
        for (const range of ranges) {
            const last = merged[merged.length - 1];
            if (last && range[0] <= last[1]) {
                last[1] = Math.max(last[1], range[1]);
            } else {
                merged.push([range[0], range[1]]);
            }
        }
        const segments: Array<{ text: string; matched: boolean }> = [];
        let cursor = 0;
        for (const [start, end] of merged) {
            if (start > cursor) {
                segments.push({ text: text.slice(cursor, start), matched: false });
            }
            segments.push({ text: text.slice(start, end), matched: true });
            cursor = end;
        }
        if (cursor < text.length) {
            segments.push({ text: text.slice(cursor), matched: false });
        }
        return segments;
    }

    async #selectSuggestion(suggestion: { prediction: any }): Promise<void> {
        this.#closeSuggestions();
        try {
            const place = suggestion.prediction.placePrediction.toPlace();
            await place.fetchFields({ fields: ['addressComponents', 'displayName'] });
            // A session ends when a place is selected; the next keystroke starts a new one.
            this.#sessionToken = null;
            const result = this.#parsePlace(place);
            // Always emit so the parent validator can react to missing fields.
            this.placeResult.emit(result);
            // If the result is missing the minimum required components (city, state, zip)
            // clear the input so the user knows the selection was incomplete.
            if (result.city && result.state && result.zipcode) {
                this.value = result.street;
            } else {
                this.value = '';
            }
            this.#cdr.markForCheck();
        } catch (error) {
            console.error('[Places] suggestion selection failed:', error);
        }
    }

    /** Maps a Places (New) Place's addressComponents into the form's AddressResult. */
    #parsePlace(place: any): AddressResult {
        const addressComponents = place.addressComponents || [];
        let streetNumber = '';
        let route = '';
        let postalCode = '';
        let postalCodeSuffix = '';
        const result: AddressResult = { ...EMPTY_ADDRESS_RESULT };
        for (const component of addressComponents) {
            if (component.types.includes('street_number')) {
                streetNumber = component.longText;
            } else if (component.types.includes('route')) {
                route = component.shortText;
            } else if (component.types.includes('locality')) {
                result.city = component.shortText;
            } else if (component.types.includes('sublocality') && !result.city) {
                result.city = component.shortText;
            } else if (component.types.includes('administrative_area_level_1')) {
                result.state = component.shortText;
            } else if (component.types.includes('administrative_area_level_2')) {
                result.county = component.shortText;
            } else if (component.types.includes('administrative_area_level_3') && !result.city) {
                result.city = component.shortText;
            } else if (component.types.includes('postal_code')) {
                postalCode = component.shortText;
            } else if (component.types.includes('postal_code_suffix')) {
                postalCodeSuffix = component.shortText;
            } else if (component.types.includes('neighborhood') && !result.city) {
                result.city = component.shortText;
            }
        }
        result.street = streetNumber ? `${streetNumber} ${route}`.trim() : (route || place.displayName || '');
        result.zipcode = postalCodeSuffix ? `${postalCode}-${postalCodeSuffix}` : postalCode;
        return result;
    }

    /**
     * Closes the dropdown AND cancels anything that could reopen it: the pending
     * debounce timer and any in-flight fetch (by bumping the request sequence so
     * a late response is discarded).
     */
    #closeSuggestions(): void {
        clearTimeout(this.#debounceTimer);
        this.#requestSequence++;
        this.#lastQuery = '';
        this.suggestions = [];
        this.highlightedIndex = -1;
        this.#updateAriaState();
        this.#cdr.markForCheck();
    }

    /** Keeps the combobox ARIA state on the inner input in sync with the dropdown. */
    #updateAriaState(): void {
        const input = this.#inputElement();
        if (!input) {
            return;
        }
        input.setAttribute('aria-expanded', String(this.suggestions.length > 0));
        if (this.highlightedIndex >= 0) {
            input.setAttribute('aria-activedescendant', `${this.elementId}-suggestion-${this.highlightedIndex}`);
        } else {
            input.removeAttribute('aria-activedescendant');
        }
    }

    #scrollHighlightedIntoView(): void {
        const dropdown = this.dropdownRef?.nativeElement;
        const highlighted = dropdown?.querySelectorAll('.suggestion')[this.highlightedIndex];
        highlighted?.scrollIntoView({ block: 'nearest' });
    }

    #inputElement(): HTMLInputElement | null {
        return this.#host.nativeElement.querySelector('input');
    }

    /**
     * Anchors the dropdown to the bottom edge of the visible field box and caps
     * its height to the space between the field and the bottom of the visual
     * viewport. On iOS the keyboard overlays the page without resizing it, so the
     * visual viewport — not the layout viewport — is what the user can see.
     */
    #sizeDropdownToViewport(): void {
        const dropdown = this.dropdownRef?.nativeElement;
        const input = this.#inputElement();
        if (!dropdown || !input) {
            return;
        }
        const inputRect = input.getBoundingClientRect();
        const hostRect = this.#host.nativeElement.getBoundingClientRect();
        dropdown.style.top = `${inputRect.bottom - hostRect.top + 6}px`;
        const viewport = window.visualViewport;
        if (viewport) {
            const visibleBottom = viewport.offsetTop + viewport.height;
            const available = visibleBottom - inputRect.bottom - 16;
            dropdown.style.maxHeight = `${Math.max(144, Math.min(288, available))}px`;
        }
    }

    #attachViewportListeners(): void {
        // Outside the Angular zone: the handler only mutates inline styles, and
        // visualViewport can fire per-frame while the keyboard animates.
        this.#zone.runOutsideAngular(() => {
            window.visualViewport?.addEventListener('resize', this.#onViewportChange);
            window.visualViewport?.addEventListener('scroll', this.#onViewportChange);
        });
    }

    #detachViewportListeners(): void {
        window.visualViewport?.removeEventListener('resize', this.#onViewportChange);
        window.visualViewport?.removeEventListener('scroll', this.#onViewportChange);
    }
}
